import { InfoCircleOutlined } from '@ant-design/icons';
import { Form, type FormInstance, Popover, Select, Typography } from 'antd';
import { isBoolean, isEmpty } from 'lodash';
import type React from 'react';
import {
  EXPLICIT_TERMS_FOUND_OPTIONS,
  RELIGIOUS_KEYWORD_FOUND,
} from '../../../../../common/constants';
import CaseInsensitiveSelect, {
  toTitleCase,
} from '../../../../../components/CaseInsensitiveSelect';
import type { VoteEnum } from '../../../../../types/common.type';
import type { ReligiousTransparencyAnalysesData } from '../ReligiousTransparencyAnalysis';
import {
  explicitTermsFoundPopoverContent,
  firstPersonPopoverContent,
  physicalContactPopoverContent,
  settingPopoverContent,
} from './SignalDetectionFieldsDetails';

const { Text } = Typography;

const infoIcon = (content: React.ReactNode) => (
  <Popover content={content} placement="top" overlayClassName="field-content">
    <InfoCircleOutlined className="signal-detection-info-icon" />
  </Popover>
);

export type SignalDetectionFormValues = {
  vote?: VoteEnum;
  religiousClientComments?: string;
  religiousDraftStanceByReligion?: string[] | null | Record<string, string>;
  religiousReligionKeywordsFound?: boolean | null;
  religiousKeywordList?: string[] | null;
  religiousReligionsNamed?: string[] | null;
  religiousFirstOrSecondPersonPronouns?: boolean | null;
  religiousOverallBiasScore?: number | null;
  religiousReasonNote?: string | null;
  religiousMiracleMentionedHow?: string[] | null;
  religiousExplicitOrImplicit?: string[] | null;
  religiousWhoSaysIt?: string[] | null;
  religiousTone?: string[] | null;
  religiousToneConfidence?: number | null;
  religiousReligiousSlangFound?: boolean | null;
  religiousDownvote?: boolean | null;
  religiousUpvote?: boolean | null;
  religiousSlangList?: string[] | null;
  religiousHistoricSlursFound?: boolean | null;
  religiousHistoricSlurList?: string[] | null;
  religiousMetaphoricReligiousTermFound?: boolean | null;
  religiousMetaphoricReligiousTermList?: string[] | null;
  religiousDisrespectTriggerList?: string[] | null;
  religiousDisrespectedReligions?: string[] | null;
  religiousComparisonFrameDetected?: boolean | null;
  religiousComparedReligions?: string[] | null;
  religiousDoctrineClaimType?: string[] | null;
  religiousFramingStrength?: string[] | null;
  religiousDisrespectTowardsReligion?: boolean | null;
};

const SignalDetectionForm: React.FC<{
  form: FormInstance;
  setIsSubmitBtnDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  selectReligiousTransparencyData:
    | ReligiousTransparencyAnalysesData
    | undefined;
}> = ({ form, setIsSubmitBtnDisabled, selectReligiousTransparencyData }) => {
  const feedbackData = selectReligiousTransparencyData?.subSection?.feedback;

  return (
    <Form
      layout="vertical"
      form={form}
      className="signal-detection-form-grid"
      onValuesChange={() => {
        setIsSubmitBtnDisabled(false);
      }}
    >
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Religious Keyword Found {infoIcon(firstPersonPopoverContent)}
            </span>
          }
          name="religiousReligionKeywordsFound"
          className={
            isBoolean(feedbackData?.religiousReligionKeywordsFound)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <Select size="middle" options={RELIGIOUS_KEYWORD_FOUND} />
        </Form.Item>
        {isBoolean(feedbackData?.religiousReligionKeywordsFound) &&
          isBoolean(selectReligiousTransparencyData?.religionKeywordsFound) &&
          !(
            feedbackData?.religiousReligionKeywordsFound ===
            selectReligiousTransparencyData?.religionKeywordsFound
          ) && (
            <div className="signal-detection-below-text">
              <Text strong>Originally:</Text>{' '}
              <Text>
                {selectReligiousTransparencyData?.religionKeywordsFound === true
                  ? 'True'
                  : selectReligiousTransparencyData?.religionKeywordsFound ===
                      false
                    ? 'False'
                    : ''}
              </Text>
            </div>
          )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Keyword List {infoIcon(physicalContactPopoverContent)}
            </span>
          }
          name="religiousKeywordList"
          className={
            !isEmpty(feedbackData?.religiousKeywordList)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <CaseInsensitiveSelect mode="tags" size="middle" options={[]} />
        </Form.Item>
        {feedbackData?.religiousKeywordList != null &&
          selectReligiousTransparencyData?.keywordList != null &&
          JSON.stringify(feedbackData?.religiousKeywordList || []) !==
            JSON.stringify(
              selectReligiousTransparencyData?.keywordList || [],
            ) && (
            <div className="signal-detection-below-text">
              <Text strong>Originally:</Text>{' '}
              <Text>
                {(selectReligiousTransparencyData?.keywordList || [])
                  .map((val: string) => toTitleCase(val))
                  .join(', ') || '--'}
              </Text>
            </div>
          )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Religious Named {infoIcon(settingPopoverContent)}
            </span>
          }
          name="religiousReligionsNamed"
          className={
            !isEmpty(feedbackData?.religiousReligionsNamed)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <CaseInsensitiveSelect mode="tags" size="middle" options={[]} />
        </Form.Item>
        {!(
          JSON.stringify(feedbackData?.religiousReligionsNamed) ===
          JSON.stringify(selectReligiousTransparencyData?.religionsNamed)
        ) && (
          <div className="signal-detection-below-text">
            <Text strong>Originally:</Text>{' '}
            <Text>
              {(selectReligiousTransparencyData?.religionsNamed || [])
                .map((val: string) => toTitleCase(val))
                .join(', ') || '--'}
            </Text>
          </div>
        )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              First or Second Person Pronounce{' '}
              {infoIcon(explicitTermsFoundPopoverContent)}
            </span>
          }
          name="religiousFirstOrSecondPersonPronouns"
          className={
            isBoolean(feedbackData?.religiousFirstOrSecondPersonPronouns)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <Select size="middle" options={EXPLICIT_TERMS_FOUND_OPTIONS} />
        </Form.Item>
        {isBoolean(feedbackData?.religiousFirstOrSecondPersonPronouns) &&
          isBoolean(
            selectReligiousTransparencyData?.firstOrSecondPersonPronouns,
          ) &&
          !(
            feedbackData?.religiousFirstOrSecondPersonPronouns ===
            selectReligiousTransparencyData?.firstOrSecondPersonPronouns
          ) && (
            <div className="signal-detection-below-text">
              <Text strong>Originally:</Text>{' '}
              <Text>
                {selectReligiousTransparencyData?.firstOrSecondPersonPronouns ===
                true
                  ? 'True'
                  : selectReligiousTransparencyData?.firstOrSecondPersonPronouns ===
                      false
                    ? 'False'
                    : ''}
              </Text>
            </div>
          )}
      </div>
    </Form>
  );
};

export default SignalDetectionForm;
